import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { getPlatformLogoPath } from "src/app/common/platformUtils";
import { IntegrationModel } from "src/app/models/api";
import { PolicyMinimalModel } from "src/app/models/api/governance/policy.model";
import { getIntegrationStatusLabel } from "src/app/models/api/integration.model";
import { ISelectOption } from "src/app/models/selectOptions";
import { FiltersQueryTransformer } from "src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer";
import { InventoryHttpService } from "src/app/services/httpServices/inventoryHttpService/inventory-http.service";
import { StoreService } from "src/app/services/infra/store/store.service";
import { FilterTypesEnum } from "../../filterTypes.enum";
import { IPageFilters } from "../../interfaces/pageFilters.interface";
import { ISelectedFilters } from "../../selectedFilters.interface";
import { MultiSelectFilters } from "../filtersTypes/multiFilter.model";

export class DashboardsFiltersModel implements IPageFilters {
    public onUpdate$ = new BehaviorSubject({} as ISelectedFilters);
    public allOptions$ = new BehaviorSubject<ISelectOption[]>([]);

    public integrationFilters: MultiSelectFilters;
    public policyFilters: MultiSelectFilters;

    private _integrationIdsToNamesMapping: { [key: string]: string } = {};

    constructor(
        private _router: Router,
        private _store: StoreService,
        private _inventoryHttpService: InventoryHttpService,
    ) {
        this._initializeFilters();

        this._integrationsOnUpdate();
        this._policiesOnUpdate();
    }

    public getSelectedFilters(): ISelectedFilters {
        return {
            integration: this.integrationFilters.getSelectedFiltersValues(),
            policy: this.policyFilters.getSelectedFiltersValues(),
        } as ISelectedFilters;
    }

    public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
        this._updateAllOptions();

        return this.allOptions$;
    }

    public getAllSelectedOptions(): ISelectOption[] {
        const selectedOptions = [...this.integrationFilters.getSelectedOptions(), ...this.policyFilters.getSelectedOptions()];

        return selectedOptions;
    }

    public updateFilterFromUserSelection(
        filterToUpdate: FilterTypesEnum | undefined,
        values: string | boolean | ISelectOption | ISelectOption[] | Date | undefined,
    ): void {
        let needUpdate = false;
        if (filterToUpdate) {
            switch (filterToUpdate) {
                default:
                    throw new Error("DashboardsFiltersModel updateFilterFromUserSelection: Not supported");
            }
        } else {
            if (values instanceof Array) {
                if (values.length == 0) {
                    needUpdate = this._clearAllMultiSelectFiltersIfNeeded();
                } else {
                    const optionsByCategory: {
                        [key: string]: ISelectOption[];
                    } = {};
                    values.forEach((option) => {
                        if (option.filterType) {
                            if (option.filterType in optionsByCategory) {
                                optionsByCategory[option.filterType].push(option);
                            } else {
                                optionsByCategory[option.filterType] = [option];
                            }
                        }
                    });

                    this._updateAllOptions();

                    needUpdate = this.integrationFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Integration] || []) || needUpdate;
                    needUpdate = this.policyFilters.updateIfNeeded(optionsByCategory[FilterTypesEnum.Policy] || []) || needUpdate;
                }
            }
        }

        if (needUpdate) {
            const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
            this._router.navigate([], {
                queryParams: { q: filtersQuery },
                queryParamsHandling: "merge",
            });
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
        switch (filter) {
            case FilterTypesEnum.Integration:
                return this.integrationFilters.getOptions$();

            case FilterTypesEnum.Policy:
                return this.policyFilters.getOptions$();

            default:
                throw new Error(`DashboardsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
        }
    }

    public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
        const optionsIdsByCategory: { [key: string]: Set<string> } = {};
        options.forEach(
            (option) => {
                if (option.filterType) {
                    if (option.filterType in optionsIdsByCategory) {
                        optionsIdsByCategory[option.filterType].add(option.id);
                    } else {
                        optionsIdsByCategory[option.filterType] = new Set([option.id]);
                    }
                }
            },
            this.getDynamicFilters().forEach((filter) => {
                optionsIdsByCategory[filter] = new Set();
            }),
        );

        if (optionsIdsByCategory[FilterTypesEnum.Integration]) {
            this.integrationFilters.updateIfNeeded(
                this.integrationFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Integration] &&
                            !optionsIdsByCategory[FilterTypesEnum.Integration].has(selectedOption.id),
                    ),
            );
        }

        if (optionsIdsByCategory[FilterTypesEnum.Policy]) {
            this.policyFilters.updateIfNeeded(
                this.policyFilters
                    .getSelectedOptions()
                    .filter(
                        (selectedOption) =>
                            optionsIdsByCategory[FilterTypesEnum.Policy] && !optionsIdsByCategory[FilterTypesEnum.Policy].has(selectedOption.id),
                    ),
            );
        }

        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
            queryParams: { q: filtersQuery },
            queryParamsHandling: "merge",
        });
        this.onUpdate$.next(this.getSelectedFilters());
    }

    public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
        switch (filter) {
            case FilterTypesEnum.Integration:
                return this.integrationFilters.getSelectedFiltersIds();

            case FilterTypesEnum.Policy:
                return this.policyFilters.getSelectedFiltersIds();

            default:
                throw new Error(`DashboardsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
        }
    }

    public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams: boolean): void {
        let needUpdate = false;

        let values;

        values = "integration" in keyValueFilters ? keyValueFilters["integration"] : [];
        needUpdate = this.integrationFilters.updateByValuesIfNeeded(values) || needUpdate;

        values = "policy" in keyValueFilters ? keyValueFilters["policy"] : [];
        needUpdate = this.policyFilters.updateByValuesIfNeeded(values) || needUpdate;

        if (needUpdate) {
            if (updateQueryParams) {
                const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
                this._router.navigate([], {
                    queryParams: { q: filtersQuery },
                    queryParamsHandling: "merge",
                });
            }
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public getDynamicFilters(): string[] {
        return [];
    }

    private _initializeFilters() {
        this.integrationFilters = new MultiSelectFilters([]);
        this.policyFilters = new MultiSelectFilters([]);

        this._updateAllOptions();
    }

    private _updateAllOptions() {
        this.allOptions$.next([...this.integrationFilters.getOptions$().value, ...this.policyFilters.getOptions$().value]);
    }

    private _integrationsOnUpdate() {
        this._store.integrations$.subscribe((integrations: IntegrationModel[]) => {
            const integrationsOptions: ISelectOption[] = [];

            integrations.forEach((integration) => {
                integrationsOptions.push({
                    id: integration.id,
                    value: integration.id,
                    displayName: `${integration.name} (${getIntegrationStatusLabel(integration.connectionStatus)})`,
                    filterType: FilterTypesEnum.Integration,
                    categoryName: FilterTypesEnum.Integration,
                    withoutBeautifyDisplayName: true,
                    iconPath: getPlatformLogoPath(integration.platform),
                } as ISelectOption);
            });

            integrationsOptions.forEach((integration) => (this._integrationIdsToNamesMapping[integration.id] = integration.displayName));

            const shouldUpdateSelectedOptions = this.integrationFilters.updateOptions(integrationsOptions);

            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _policiesOnUpdate() {
        this._store.accountMinimalPolicies$.subscribe((policies: PolicyMinimalModel[]) => {
            const policiesOptions: ISelectOption[] = [];

            policies.forEach((policy) => {
                const policyIntegrationName = this._integrationIdsToNamesMapping[policy.integrationId];
                if (policyIntegrationName !== undefined) {
                    policiesOptions.push({
                        id: policy.id + "_" + policyIntegrationName,
                        value: policy.id,
                        displayName: `${policy.name} (Integration: ${policyIntegrationName})`,
                        filterType: FilterTypesEnum.Policy,
                        categoryName: FilterTypesEnum.Policy,
                        withoutBeautifyDisplayName: true,
                        iconPath: getPlatformLogoPath(policy.platform),
                    } as ISelectOption);
                }
            });

            const shouldUpdateSelectedOptions = this.policyFilters.updateOptions(policiesOptions);

            this._updateAllOptions();
            if (shouldUpdateSelectedOptions) {
                this.onUpdate$.next(this.getSelectedFilters());
            }
        });
    }

    private _clearAllMultiSelectFiltersIfNeeded(): boolean {
        const needUpdate = false;

        return needUpdate || this.integrationFilters.updateIfNeeded([]) || this.policyFilters.updateIfNeeded([]);
    }
}
